<template>
  <div class="dashboard-snippet">
    <div class="dashboard-snippet__head">
      <div class="dashboard-snippet__title">
        <span class="dashboard-snippet__number">{{ gathering.SubmitCode }}.</span>
        <div class="dashboard-snippet__picture" v-if="gathering.PhotoUri" :style="{ backgroundImage: 'url(' + gathering.PhotoUri + ')' }"></div>

        <div class="editable-input">
          <div v-if="!showRenameGathering" class="editable-input__edit-name">
            <a :href="gathering.Url" class="editable-input__name" target="_blank">
              {{ gathering.Name }}
            </a>
            <button class="a-btn-edit editable-input__btn-edit" @click="showRenamingGathering" :title="$t('gathering-rename-btn')">
              <img src="@/assets/edit-purple.svg" :alt="$t('gathering-rename-btn')" />
            </button>
          </div>

          <div v-else>
            <span class="a-form-control editable-input__rename">
              <input class="editable-input__rename-input" v-model="gatheringName" />
              <button class="editable-input__rename-confirm" @click="renameGathering" type="button">{{ $t('gathering-confirm-rename-btn') }}</button>
              <button class="editable-input__rename-cancel" @click="cancelRenameGathering" type="button">{{ $t('gathering-cancel-rename-btn') }}</button>
            </span>
          </div>
        </div>

        <p class="dashboard-snippet__type">{{ typeLabel }}</p>
      </div>

      <div class="dashboard-snippet__actions">
        <button class="a-btn-primary a-btn-primary--small" @click="goToEditGathering" type="button">{{ $t('gathering-edit-btn') }}</button>

        <template v-if="!gathering.IsPublish && !gathering.IsRefused">
          <button class="a-btn-primary a-btn-primary--small a-btn-primary--color3 a-btn-primary--ico-cancel" @click="showRefuseGatheringPanel" type="button">
            {{ $t('gathering-refuse-btn') }}
          </button>
          <button class="a-btn-primary a-btn-primary--small a-btn-primary--color1 a-btn-primary--ico-confirm" @click="showAuthorizeGatheringPanel" type="button">
            {{ $t('gathering-authorize-btn') }}
          </button>

          <div class="o-panel" v-bind:class="{ 'o-panel--open': isAuthorizeGatheringPanelOpen }">
            <div class="o-panel__head">
              <h2 class="o-panel__title">{{ $t('gathering-authorize-title') }}</h2>
              <button type="button" class="o-panel__close" @click="hideAuthorizeGatheringPanel">{{ $t('panel-close') }}</button>
            </div>
            <div class="o-panel__content">
              <confirmation-popup :text="$t('gathering-authorization-confirmation-text')" :actions="authorizationActions" />
            </div>
          </div>

          <div class="o-panel__overlay" @click="hideAuthorizeGatheringPanel"></div>

          <div class="o-panel" v-bind:class="{ 'o-panel--open': canNotAuthorizeGatheringPanelOpen }">
            <div class="o-panel__head">
              <h2 class="o-panel__title">{{ $t('gathering-can-not-authorize-title') }}</h2>
              <button type="button" class="o-panel__close" @click="hideCanNotAuthorizeGatheringPanel">{{ $t('panel-close') }}</button>
            </div>
            <div class="o-panel__content">
              <confirmation-popup :text="$t('gathering-can-not-authorize-text')" :actions="canNotAuthorizeAction" />
            </div>
          </div>

          <div class="o-panel__overlay" @click="hideCanNotAuthorizeGatheringPanel"></div>

          <div class="o-panel" v-bind:class="{ 'o-panel--open': isRefuseGatheringPanelOpen }">
            <div class="o-panel__head">
              <h2 class="o-panel__title">{{ $t('gathering-refuse-title') }}</h2>
              <button type="button" class="o-panel__close" @click="hideRefuseGatheringPanel">{{ $t('panel-close') }}</button>
            </div>
            <div class="o-panel__content">
              <confirmation-popup :text="$t('gathering-refuse-confirmation-text')" :actions="refuseActions" />
            </div>
          </div>

          <div class="o-panel__overlay" @click="hideRefuseGatheringPanel"></div>
        </template>

        <template v-else>
          <!-- add to recreate another panel for Authorize/Refuse in this div -->
          <div class="o-panel" v-bind:class="{ 'o-panel--open': isAuthorizeGatheringPanelOpen }">
            <div class="o-panel__head">
              <h2 class="o-panel__title">{{ $t('gathering-authorize-title') }}</h2>
              <button type="button" class="o-panel__close" @click="hideAuthorizeGatheringPanel">{{ $t('panel-close') }}</button>
            </div>
            <div class="o-panel__content">
              <confirmation-popup :text="$t('gathering-authorization-confirmation-text')" :actions="authorizationActions" />
            </div>
          </div>

          <div class="o-panel__overlay" @click="hideAuthorizeGatheringPanel"></div>

          <div class="o-panel" v-bind:class="{ 'o-panel--open': canNotAuthorizeGatheringPanelOpen }">
            <div class="o-panel__head">
              <h2 class="o-panel__title">{{ $t('gathering-can-not-authorize-title') }}</h2>
              <button type="button" class="o-panel__close" @click="hideCanNotAuthorizeGatheringPanel">{{ $t('panel-close') }}</button>
            </div>
            <div class="o-panel__content">
              <confirmation-popup :text="$t('gathering-can-not-authorize-text')" :actions="canNotAuthorizeAction" />
            </div>
          </div>

          <div class="o-panel__overlay" @click="hideCanNotAuthorizeGatheringPanel"></div>

          <div class="o-panel" v-bind:class="{ 'o-panel--open': isUnpublishGatheringPanelOpen }">
            <div class="o-panel__head">
              <h2 class="o-panel__title">{{ $t('gathering-unpublish-title') }}</h2>
              <button type="button" class="o-panel__close" @click="hideUnpublishGatheringPanel">{{ $t('panel-close') }}</button>
            </div>
            <div class="o-panel__content">
              <confirmation-popup :text="$t('gathering-unpublish-confirmation-text')" :actions="unpublishActions" />
            </div>
          </div>

          <div class="o-panel__overlay" @click="hideUnpublishGatheringPanel"></div>

          <div class="o-panel" v-bind:class="{ 'o-panel--open': isCancelGatheringPanelOpen }">
            <div class="o-panel__head">
              <h2 class="o-panel__title">{{ $t('gathering-cancel-title') }}</h2>
              <button type="button" class="o-panel__close" @click="hideCancelGatheringPanel">{{ $t('panel-close') }}</button>
            </div>
            <div class="o-panel__content">
              <confirmation-popup :text="$t('gathering-cancel-confirmation-text')" :actions="cancelActions" />
            </div>
          </div>

          <div class="o-panel__overlay" @click="hideCancelGatheringPanel"></div>

          <div class="o-panel" v-bind:class="{ 'o-panel--open': isReactivateGatheringPanelOpen }">
            <div class="o-panel__head">
              <h2 class="o-panel__title">{{ $t('gathering-reactivate-title') }}</h2>
              <button type="button" class="o-panel__close" @click="hideReactivateGatheringPanel">{{ $t('panel-close') }}</button>
            </div>
            <div class="o-panel__content">
              <confirmation-popup :text="$t('gathering-reactivate-confirmation-text')" :actions="reactivateActions" />
            </div>
          </div>

          <div class="o-panel__overlay" @click="hideReactivateGatheringPanel"></div>

          <button
            class="a-btn-primary a-btn-primary--small a-btn-primary--gray a-btn-primary--ico-retract"
            v-if="gathering.IsPublish || gathering.IsRefused"
            @click="showUnpublishGatheringPanel"
            type="button"
          >
            {{ $t('gathering-unpublish-btn') }}
          </button>
          <button
            class="a-btn-primary a-btn-primary--small a-btn-primary--color3 a-btn-primary--ico-cancel"
            v-if="gathering.IsPublish && !gathering.IsCancelled"
            @click="showCancelGatheringPanel"
            type="button"
          >
            {{ $t('gathering-cancel-btn') }}
          </button>
          <button
            class="a-btn-primary a-btn-primary--small a-btn-primary--color1 a-btn-primary--ico-confirm"
            v-if="gathering.IsRefused || gathering.IsCancelled"
            @click="showAuthorizeGatheringPanel"
            type="button"
          >
            {{ $t('gathering-authorize-btn') }}
          </button>
        </template>
      </div>
    </div>

    <div class="dashboard-snippet__grid">
      <div class="dashboard-snippet__places">
        <span class="dashboard-snippet__item-title">{{ $t('gathering-list-numberAvailablePlaces-label') }}</span>
        <p v-if="gathering.NbKiosk > 0" class="dashboard-snippet__item-desc">{{ `${gathering.BusinessIds.length}/${gathering.NbKiosk}` }}</p>
        <p v-else class="dashboard-snippet__item-desc">{{ $t('gathering-places-to-be-determined-text') }}</p>
      </div>
      <div class="dashboard-snippet__location-type">
        <span class="dashboard-snippet__item-title">{{ $t('gathering-list-location-type-label') }}</span>
        <p v-if="gathering.LocationType !== ''" class="dashboard-snippet__item-desc">{{ gathering.LocationType }}</p>
        <p v-else class="dashboard-snippet__item-desc">{{ $t('gathering-to-be-determined-text') }}</p>
      </div>
      <div class="dashboard-snippet__location">
        <span class="dashboard-snippet__item-title">{{ $t('gathering-list-location-label') }}</span>
        <p v-if="gathering.City !== ''" class="dashboard-snippet__item-desc">{{ gathering.City }}</p>
        <p v-else class="dashboard-snippet__item-desc">{{ $t('gathering-to-be-determined-text') }}</p>
      </div>
      <div class="dashboard-snippet__organizer">
        <span class="dashboard-snippet__item-title">{{ $t('gathering-list-organisator-label') }}</span>
        <p class="dashboard-snippet__item-desc">
          {{ gathering.OrganisatorName }}
        </p>
        <p class="dashboard-snippet__item-desc">
          <a :href="`mailto:${gathering.OrganisatorEmail}`">{{ gathering.OrganisatorEmail }}</a>
        </p>
      </div>
      <div class="dashboard-snippet__form-comp">
        <span class="dashboard-snippet__item-title">{{ $t('select-authorization-complementary-form-placeholder') }}</span>
        <select
          id="authorizationComplementaryForm"
          class="a-form-control a-form-control--inline dashboard-snippet__item-desc"
          v-model="gathering.AuthorizationComplementaryForm"
          @change="updateAuthorizationComplementaryForm"
          :disabled="disableAuthorizationSelects"
        >
          <option v-for="value in authorizationComplementaryFormValues" :disabled="value.value === 0" :value="value.value" :key="'authorization-complementary-form' + value.value">{{
            value.label
          }}</option>
        </select>
      </div>
      <div class="dashboard-snippet__meeting">
        <span class="dashboard-snippet__item-title">{{ $t('select-authorization-meeting-placeholder') }}</span>
        <select
          id="authorizationMeeting"
          class="a-form-control a-form-control--inline dashboard-snippet__item-desc"
          v-model="gathering.AuthorizationMeeting"
          @change="updateAuthorizationMeeting"
          :disabled="disableAuthorizationSelects"
        >
          <option v-for="value in authorizationMeetingValues" :disabled="value.value === 0" :value="value.value" :key="'authorization-meeting' + value.value">{{ value.label }}</option>
        </select>
      </div>
      <div class="dashboard-snippet__agreement">
        <span class="dashboard-snippet__item-title">{{ $t('select-authorization-agreement-placeholder') }}</span>
        <select
          id="authorizationAgreement"
          class="a-form-control a-form-control--inline dashboard-snippet__item-desc"
          v-model="gathering.AuthorizationAgreement"
          @change="updateAuthorizationAgreement"
          :disabled="disableAuthorizationSelects"
        >
          <option v-for="value in authorizationAgreementValues" :disabled="value.value === 0" :value="value.value" :key="'authorization-agreement' + value.value">{{ value.label }}</option>
        </select>
      </div>
      <div class="editable-input editable-input--input-height dashboard-snippet__note">
        <div v-if="!showEditAuthorizationNotes" class="editable-input__edit-name">
          <span class="editable-input__name editable-input__name--small">{{ getAuthorizationNotes }}</span>
          <button class="a-btn-edit editable-input__btn-edit" @click="showEditingAuthorizationNotes" :title="$t('gathering-edit-authorization-notes-btn')">
            <img src="@/assets/edit-purple.svg" :alt="$t('gathering-edit-authorization-notes-btn')" />
          </button>
        </div>

        <template v-else>
          <span class="a-form-control editable-input__rename editable-input__rename--grow">
            <input class="editable-input__rename-input" v-model="authorizationNotes" />
            <button class="editable-input__rename-confirm" @click="updateGatheringAuthorizationNotes" type="button">{{ $t('gathering-confirm-edit-authorization-notes-btn') }}</button>
            <button class="editable-input__rename-cancel" @click="cancelEditAuthorizationNotes" type="button">{{ $t('gathering-cancel-edit-authorization-notes-btn') }}</button>
          </span>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import ConfirmationPopup from '@/components/popup/confirmation-popup'
import i18n from '@/helpers/i18n'

import GatheringService from '@/services/gathering'
import LoaderService from '@/services/loader'
import router from '@/router'

import { URL_EDIT_GATHERING } from '@/consts/urls'

export default {
  components: {
    ConfirmationPopup
  },
  props: {
    gathering: {
      type: Object,
      required: true
    },
    disableAuthorizationSelects: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data() {
    return {
      isAuthorizeGatheringPanelOpen: false,
      canNotAuthorizeGatheringPanelOpen: false,
      authorizationActions: [
        { key: 'authorizeGathering_yes', label: this.$t('popup_confirm_label'), click: this.authorizeGathering },
        { key: 'authorizeGathering_no', label: this.$t('popup_dismiss_label'), click: this.hideAuthorizeGatheringPanel }
      ],
      canNotAuthorizeAction: [{ key: 'canNotAuthorizeGathering_ok', label: this.$t('popup_okay_label'), click: this.hideCanNotAuthorizeGatheringPanel }],
      isRefuseGatheringPanelOpen: false,
      refuseActions: [
        { key: 'refuseGathering_yes', label: this.$t('popup_confirm_label'), click: this.refuseGathering },
        { key: 'refuseGathering_no', label: this.$t('popup_dismiss_label'), click: this.hideRefuseGatheringPanel }
      ],
      isCancelGatheringPanelOpen: false,
      cancelActions: [
        { key: 'cancelGathering_yes', label: this.$t('popup_confirm_label'), click: this.cancelGathering },
        { key: 'cancelGathering_no', label: this.$t('popup_dismiss_label'), click: this.hideCancelGatheringPanel }
      ],
      isReactivateGatheringPanelOpen: false,
      reactivateActions: [
        { key: 'reactivateGathering_yes', label: this.$t('popup_confirm_label'), click: this.reactivateGathering },
        { key: 'reactivateGathering_no', label: this.$t('popup_dismiss_label'), click: this.hideReactivateGatheringPanel }
      ],
      isUnpublishGatheringPanelOpen: false,
      unpublishActions: [
        { key: 'unpublishGathering_yes', label: this.$t('popup_confirm_label'), click: this.unpublishGathering },
        { key: 'unpublishGathering_no', label: this.$t('popup_dismiss_label'), click: this.hideUnpublishGatheringPanel }
      ],
      authorizationComplementaryFormValues: [
        { value: 1, label: this.$t('select-authorization-complementary-form-unnecessary'), valid: true },
        { value: 2, label: this.$t('select-authorization-complementary-form-sent') },
        { value: 3, label: this.$t('select-authorization-complementary-form-done'), valid: true }
      ],
      authorizationMeetingValues: [
        { value: 1, label: this.$t('select-authorization-meeting-unnecessary'), valid: true },
        { value: 2, label: this.$t('select-authorization-meeting-sent') },
        { value: 3, label: this.$t('select-authorization-meeting-fixed') },
        { value: 4, label: this.$t('select-authorization-meeting-done'), valid: true }
      ],
      authorizationAgreementValues: [
        { value: 1, label: this.$t('select-authorization-agreement-unnecessary'), valid: true },
        { value: 2, label: this.$t('select-authorization-agreement-to-do') },
        { value: 3, label: this.$t('select-authorization-agreement-sent') },
        { value: 4, label: this.$t('select-authorization-agreement-signed'), valid: true }
      ],
      showRenameGathering: false,
      gatheringName: '',
      showEditAuthorizationNotes: false,
      authorizationNotes: ''
    }
  },
  computed: {
    typeLabel: function() {
      return this.gathering.IsPublic ? i18n.instance().t('label-gathering-type-public') : i18n.instance().t('label-gathering-type-private')
    },
    getAuthorizationNotes: function() {
      return this.gathering.AuthorizationNotes !== null && this.gathering.AuthorizationNotes !== undefined && this.gathering.AuthorizationNotes.trim() !== ''
        ? this.gathering.AuthorizationNotes
        : i18n.instance().t('gathering-authorization-notes-placeholder')
    }
  },
  methods: {
    showAuthorizeGatheringPanel: function() {
      const canAuthorize =
        this.authorizationComplementaryFormValues.find(value => value.value === this.gathering.AuthorizationComplementaryForm).valid &&
        this.authorizationMeetingValues.find(value => value.value === this.gathering.AuthorizationMeeting).valid &&
        this.authorizationAgreementValues.find(value => value.value === this.gathering.AuthorizationAgreement).valid

      if (canAuthorize) {
        this.isAuthorizeGatheringPanelOpen = true
      } else {
        this.canNotAuthorizeGatheringPanelOpen = true
      }
    },
    hideAuthorizeGatheringPanel: function() {
      this.isAuthorizeGatheringPanelOpen = false
    },
    hideCanNotAuthorizeGatheringPanel: function() {
      this.canNotAuthorizeGatheringPanelOpen = false
    },
    authorizeGathering: async function() {
      LoaderService.showLoader()

      let response = await GatheringService.authorizeGathering(this.gathering.Id)
      this.gathering.IsPublish = response.IsPublish
      this.gathering.IsRefused = false
      this.gathering.IsCancelled = false
      this.isAuthorizeGatheringPanelOpen = false

      this.$emit('updatedGathering')

      LoaderService.hideLoader()
    },
    showRefuseGatheringPanel: function() {
      this.isRefuseGatheringPanelOpen = true
    },
    hideRefuseGatheringPanel: function() {
      this.isRefuseGatheringPanelOpen = false
    },
    refuseGathering: async function() {
      LoaderService.showLoader()

      let response = await GatheringService.refuseGathering(this.gathering.Id)
      this.gathering.IsRefused = response.IsRefused
      this.gathering.IsCancelled = false
      this.isRefuseGatheringPanelOpen = false

      this.$emit('updatedGathering')

      LoaderService.hideLoader()
    },
    showCancelGatheringPanel: function() {
      this.showMenu = false
      this.isCancelGatheringPanelOpen = true
    },
    hideCancelGatheringPanel: function() {
      this.isCancelGatheringPanelOpen = false
    },
    cancelGathering: async function() {
      LoaderService.showLoader()

      let response = await GatheringService.cancelGathering(this.gathering.Id)
      this.gathering.IsCancelled = response.IsCancelled
      this.gathering.IsRefused = false
      this.isCancelGatheringPanelOpen = false

      this.$emit('updatedGathering')

      LoaderService.hideLoader()
    },
    showReactivateGatheringPanel: function() {
      this.showMenu = false
      this.isReactivateGatheringPanelOpen = true
    },
    hideReactivateGatheringPanel: function() {
      this.isReactivateGatheringPanelOpen = false
    },
    reactivateGathering: async function() {
      LoaderService.showLoader()

      let response = await GatheringService.reactivateGathering(this.gathering.Id)
      this.gathering.IsCancelled = response.IsCancelled
      this.gathering.IsRefused = response.IsRefused
      this.isReactivateGatheringPanelOpen = false

      this.$emit('updatedGathering')

      LoaderService.hideLoader()
    },
    showRenamingGathering: function() {
      this.showMenu = false
      this.gatheringName = this.gathering.Name
      this.showRenameGathering = true
    },
    cancelRenameGathering: function() {
      this.showRenameGathering = false
    },
    goToEditGathering: function() {
      router.push({ name: URL_EDIT_GATHERING, params: { id: this.gathering.Id } })
    },
    renameGathering: async function() {
      LoaderService.showLoader()

      let response = await GatheringService.renameGathering(this.gathering.Id, this.gatheringName)
      this.gathering.Name = response.Name
      this.gathering.Url = response.Url
      this.showRenameGathering = false

      this.$emit('updatedGathering')

      LoaderService.hideLoader()
    },
    showEditingAuthorizationNotes: function() {
      this.showMenu = false
      this.authorizationNotes = this.gathering.AuthorizationNotes
      this.showEditAuthorizationNotes = true
    },
    cancelEditAuthorizationNotes: function() {
      this.showEditAuthorizationNotes = false
    },
    updateGatheringAuthorizationNotes: async function() {
      LoaderService.showLoader()

      let response = await GatheringService.updateGatheringAuthorizationNotes(this.gathering.Id, this.authorizationNotes)
      this.gathering.AuthorizationNotes = response.AuthorizationNotes
      this.showEditAuthorizationNotes = false

      this.$emit('updatedGathering')

      LoaderService.hideLoader()
    },
    showUnpublishGatheringPanel: function() {
      this.showMenu = false
      this.isUnpublishGatheringPanelOpen = true
    },
    hideUnpublishGatheringPanel: function() {
      this.isUnpublishGatheringPanelOpen = false
    },
    unpublishGathering: async function() {
      LoaderService.showLoader()

      let response = await GatheringService.unpublishGathering(this.gathering.Id)
      this.gathering.IsPublish = response.IsPublish
      this.gathering.IsRefused = false
      this.gathering.IsCancelled = false
      this.isUnpublishGatheringPanelOpen = false

      this.$emit('updatedGathering')

      LoaderService.hideLoader()
    },
    updateAuthorizationComplementaryForm: async function() {
      LoaderService.showLoader()
      const response = await GatheringService.updateGatheringAuthorizationComplementaryForm(this.gathering.Id, this.gathering.AuthorizationComplementaryForm)
      this.gathering.AuthorizationComplementaryForm = response.AuthorizationComplementaryForm
      LoaderService.hideLoader()
    },
    updateAuthorizationMeeting: async function() {
      LoaderService.showLoader()
      const response = await GatheringService.updateGatheringAuthorizationMeeting(this.gathering.Id, this.gathering.AuthorizationMeeting)
      this.gathering.AuthorizationMeeting = response.AuthorizationMeeting
      LoaderService.hideLoader()
    },
    updateAuthorizationAgreement: async function() {
      LoaderService.showLoader()
      const response = await GatheringService.updateGatheringAuthorizationAgreement(this.gathering.Id, this.gathering.AuthorizationAgreement)
      this.gathering.AuthorizationAgreement = response.AuthorizationAgreement
      LoaderService.hideLoader()
    },
    updateAuthorizationNotes: async function() {
      LoaderService.showLoader()
      const response = await GatheringService.updateGatheringAuthorizationNotes(this.gathering.Id, this.gathering.AuthorizationAgreement)
      this.gathering.AuthorizationNotes = response.AuthorizationNotes
      LoaderService.hideLoader()
    }
  }
}
</script>

<style lang="scss">
.dashboard-snippet {
  border-left: 8px solid color('primary');
  padding: spacing('thin') 0 spacing('thin') spacing('light');
  margin-bottom: spacing('normal');

  &__head {
    display: flex;
    align-items: flex-start;
    flex-wrap: wrap;
    padding-bottom: spacing('thin');
    margin-bottom: spacing('light');
    border-bottom: 2px solid #c9c9c9;
  }

  &__title {
    display: flex;
    align-items: flex-start;
    margin-bottom: spacing('thin');
  }

  &__number {
    margin-right: spacing('thin');
    line-height: 1.3;
  }

  &__picture {
    display: none;
    border-radius: 50%;
    height: 60px;
    margin-right: 12px;
    overflow: hidden;
    position: relative;
    width: 60px;
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;
    flex-shrink: 0;
    top: -15px;
  }

  &__type {
    border-radius: 4px;
    border: 1px solid #c9c9c9;
    font-size: 14px;
    padding: 2px 8px;
    margin: 0 0 0 spacing('thin');
  }

  &__actions {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    align-items: center;
    margin-left: auto;
    margin-right: 0;

    & > button {
      margin-left: 4px;
      margin-bottom: 4px;
    }
  }

  &__grid {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr 1fr;
    grid-template-areas:
      'places location-type location-type location-type'
      'location location location location'
      'organizer organizer organizer organizer'
      'third-party third-party third-party third-party'
      'resource resource resource resource';
    column-gap: spacing('light');
    row-gap: spacing('light');
    margin-bottom: spacing('light');
  }

  &__places {
    grid-area: places;
  }

  &__location-type {
    grid-area: location-type;
  }

  &__location {
    grid-area: location;
  }

  &__organizer {
    grid-area: organizer;
  }

  &__third-party {
    grid-area: third-party;
  }

  &__resource {
    grid-area: resource;
  }

  &__form-comp {
    grid-area: form-comp;
  }

  &__meeting {
    grid-area: meeting;
  }

  &__agreement {
    grid-area: agreement;
  }

  &__note {
    grid-area: note;
  }

  &__item-title {
    font-size: 16px;
    font-weight: bold;
    line-height: 1.3;
  }

  &__item-desc {
    font-size: 18px;
    line-height: 1.3;
    margin-bottom: 0;
  }
}

@media screen and (min-width: 568px) {
  .dashboard-snippet {
    &__picture {
      display: block;
    }

    &__grid {
      grid-template-columns: 1fr 1fr;
      grid-template-areas:
        'places location-type'
        'organizer location'
        'third-party resource';
    }

    &__item-desc {
      font-size: $font-size-base;
    }
  }
}

@media screen and (min-width: 992px) {
  .dashboard-snippet {
    &__grid {
      grid-template-columns: 4fr 4fr 4fr 1fr 1fr 1fr 1fr 1fr;
      grid-template-areas:
        'places location-type location organizer organizer organizer . .'
        'third-party third-party resource resource resource resoucre . .'
        'form-comp meeting agreement note note note note note';
    }
  }
}

@media screen and (min-width: 1200px) {
  .dashboard-snippet {
    &__note {
      margin-left: spacing('thin');
      width: 100%;
      flex-grow: 1;
    }
  }
}

.editable-input {
  &--input-height {
    min-height: 123px;
    display: flex;
    align-items: center;
  }

  &__name {
    align-items: center;
    display: flex;

    &--small {
      font-size: 16px;
      position: relative;
      top: 3px;
    }
  }

  &__edit-name {
    display: flex;
    flex-wrap: nowrap;
    align-items: flex-start;
    justify-content: flex-start;
  }

  &__btn-edit {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__name {
    line-height: 1.3;
  }

  &__rename {
    align-items: center;
    display: flex;

    &--grow {
      flex-grow: 1;
      max-width: none;
    }

    &-input {
      border: 0;
      outline: none;
      width: calc(100% - 40px - 40px);
    }

    &-confirm {
      appearance: none;
      background: #00c888 url(~@/assets/confirm.svg) no-repeat center center;
      background-size: 20px 20px;
      border: 0;
      box-shadow: none;
      color: transparent;
      cursor: pointer;
      font: 0/0 a;
      height: 50px;
      padding: 0;
      position: relative;
      right: -16px;
      text-shadow: none;
      white-space: nowrap;
      width: 40px;
    }

    &-cancel {
      appearance: none;
      background: #ea5244 url(~@/assets/cancel.svg) no-repeat center center;
      background-size: 20px 20px;
      border: 0;
      border-radius: 0px 8px 8px 0px;
      box-shadow: none;
      color: transparent;
      cursor: pointer;
      font: 0/0 a;
      height: 50px;
      padding: 0;
      position: relative;
      right: -16px;
      text-shadow: none;
      white-space: nowrap;
      width: 40px;
    }
  }
}
</style>
